import * as THREE from 'three';
import { RenderQuality, getQualitySettings, type RenderQualitySettings } from './RenderQuality';
import { reportMaxAnisotropy } from './TextureCapabilities';

/**
 * Internal renderer abstraction (spec §4 "Rendering Evolution"). Nothing outside `src/rendering/`
 * should import a concrete Three.js renderer. This interface is intentionally minimal so a future
 * `WebGPURenderer` implementation can be dropped in without touching editor/flora/project/simulation
 * code.
 */
export interface CaptureSize {
  width: number;
  height: number;
  /**
   * Multisampling. Defaults to the quality preset's setting. Semantic passes (a species ID map)
   * must set this false — antialiasing blends neighbouring key colours into intermediate values
   * that correspond to no species at all.
   */
  antialias?: boolean;
}

export interface Renderer {
  readonly domElement: HTMLCanvasElement;
  readonly quality: RenderQuality;
  render(scene: THREE.Scene, camera: THREE.Camera): void;
  setSize(width: number, height: number): void;
  setQuality(quality: RenderQuality): void;
  getSettings(): RenderQualitySettings;
  /**
   * Render one frame off-screen and return its RGBA pixels, bottom-row-first (OpenGL order).
   *
   * Photo capture goes through a render target rather than reading the canvas back: the live canvas
   * is created without `preserveDrawingBuffer`, so `toBlob` after a present is unreliable, and a
   * target lets a capture exceed the viewport's resolution without disturbing what the user sees.
   *
   * Optional so injected test renderers need not implement a GPU path.
   */
  renderToPixels?(scene: THREE.Scene, camera: THREE.Camera, size: CaptureSize): Uint8Array;
  /**
   * Prefilter an equirectangular texture into a cube map suitable for `Scene.environment`.
   *
   * The work is a GPU convolution — a mip chain where each level is the source blurred to the lobe
   * width of a rougher material — so it can only be done by the renderer, and it is on this
   * interface rather than done by the caller so that nothing outside `src/rendering/` needs to know
   * that a `PMREMGenerator` (or, on a WebGPU renderer, something else entirely) is involved.
   *
   * The caller owns the returned texture and must dispose it. Optional, and null when unsupported,
   * so a headless test renderer can decline and the scene simply renders without image-based fill.
   */
  prefilterEnvironment?(source: THREE.Texture): THREE.Texture | null;
  dispose(): void;
}

export interface WebGLRendererOptions {
  /** Existing canvas to render into; one is created if omitted. */
  canvas?: HTMLCanvasElement;
  /** Initial quality preset. */
  quality?: RenderQuality;
  /** Injected renderer factory (used by tests to supply a mock). */
  createRenderer?: (params: THREE.WebGLRendererParameters) => THREE.WebGLRenderer;
}

/**
 * Concrete WebGL implementation of {@link Renderer} wrapping Three.js `WebGLRenderer`.
 */
export class WebGLRendererAdapter implements Renderer {
  private readonly renderer: THREE.WebGLRenderer;
  private currentQuality: RenderQuality;
  private width = 1;
  private height = 1;
  /** Built on first use — see {@link prefilterEnvironment}. */
  private pmrem: THREE.PMREMGenerator | null = null;

  constructor(options: WebGLRendererOptions = {}) {
    this.currentQuality = options.quality ?? RenderQuality.Medium;
    const settings = getQualitySettings(this.currentQuality);
    const factory =
      options.createRenderer ?? ((params) => new THREE.WebGLRenderer(params));

    const params: THREE.WebGLRendererParameters = {
      antialias: settings.antialias,
      alpha: false,
      powerPreference: 'high-performance',
    };
    if (options.canvas) {
      params.canvas = options.canvas;
    }
    this.renderer = factory(params);
    this.renderer.setClearColor(0x87b6e8, 1);
    this.applyQuality(settings);
    // Optional-chained because an injected test renderer is a mock with no GL context behind it.
    reportMaxAnisotropy(this.renderer.capabilities?.getMaxAnisotropy?.());
  }

  get domElement(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  get quality(): RenderQuality {
    return this.currentQuality;
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    this.renderer.render(scene, camera);
  }

  setSize(width: number, height: number): void {
    this.width = Math.max(1, Math.floor(width));
    this.height = Math.max(1, Math.floor(height));
    const settings = getQualitySettings(this.currentQuality);
    const ratio = this.resolvePixelRatio(settings.maxPixelRatio);
    this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(this.width, this.height, false);
  }

  setQuality(quality: RenderQuality): void {
    this.currentQuality = quality;
    this.applyQuality(getQualitySettings(quality));
    // Re-apply size so pixel ratio changes take effect.
    this.setSize(this.width, this.height);
  }

  getSettings(): RenderQualitySettings {
    return getQualitySettings(this.currentQuality);
  }

  /**
   * Render off-screen at an arbitrary resolution and read the pixels back.
   *
   * The target is allocated per call and disposed immediately — captures are rare and can be large,
   * so holding one resident would cost more VRAM than it saves. The live canvas size and the
   * renderer's current target are both restored, so the on-screen view is untouched.
   */
  renderToPixels(scene: THREE.Scene, camera: THREE.Camera, size: CaptureSize): Uint8Array {
    const width = Math.max(1, Math.floor(size.width));
    const height = Math.max(1, Math.floor(size.height));

    const antialias = size.antialias ?? this.getSettings().antialias;
    const target = new THREE.WebGLRenderTarget(width, height, {
      // Match the canvas so a capture is colour-identical to what is on screen.
      colorSpace: THREE.SRGBColorSpace,
      samples: antialias ? 4 : 0,
      depthBuffer: true,
    });

    // Pixel ratio is deliberately untouched: it scales the canvas drawing buffer, not render
    // targets, so changing it here would only churn the live view.
    const previousTarget = this.renderer.getRenderTarget();

    try {
      this.renderer.setRenderTarget(target);
      this.renderer.clear();
      this.renderer.render(scene, camera);

      const pixels = new Uint8Array(width * height * 4);
      this.renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
      return pixels;
    } finally {
      this.renderer.setRenderTarget(previousTarget);
      target.dispose();
    }
  }

  /**
   * PMREM-convolve an equirectangular sky into an environment cube map.
   *
   * The generator is held rather than made per call: it compiles a handful of shaders and allocates
   * its own render targets on construction, and the sky is re-prefiltered every time the time of day
   * moves far enough to change its colour. Building one per call turned a slider drag into a
   * stutter.
   */
  prefilterEnvironment(source: THREE.Texture): THREE.Texture | null {
    if (!this.pmrem) {
      this.pmrem = new THREE.PMREMGenerator(this.renderer);
      this.pmrem.compileEquirectangularShader();
    }
    return this.pmrem.fromEquirectangular(source).texture;
  }

  dispose(): void {
    this.pmrem?.dispose();
    this.pmrem = null;
    this.renderer.dispose();
    this.renderer.forceContextLoss?.();
  }

  private applyQuality(settings: RenderQualitySettings): void {
    this.renderer.shadowMap.enabled = settings.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
  }

  private resolvePixelRatio(max: number): number {
    const dpr =
      typeof globalThis !== 'undefined' && 'devicePixelRatio' in globalThis
        ? (globalThis as { devicePixelRatio?: number }).devicePixelRatio ?? 1
        : 1;
    return Math.min(dpr, max);
  }
}
